import { Plugin, PluginKey } from "@tiptap/pm/state";
import type { EditorView } from "@tiptap/pm/view";
import { Extension } from "@tiptap/react";
import { applyCanonicalTopLevelFieldAttrs } from "../schemas/schema-metadata";
import type { DocumentSchemaConfig } from "../schemas/types";

export interface SchemaAwareDeleteOptions {
  schemaConfig: DocumentSchemaConfig | null;
}

export const schemaAwareDeletePluginKey = new PluginKey("schemaAwareDelete");

function shouldHandleSchemaAwareDelete(
  view: EditorView,
  eventType: "backward" | "forward" | "cut"
): boolean {
  const { selection } = view.state;
  if (selection.empty) {
    return false;
  }

  if (eventType === "cut" && selection.content().size === 0) {
    return false;
  }

  return true;
}

function deleteSelectionWithCanonicalFieldAttrs(
  view: EditorView,
  schemaConfig: DocumentSchemaConfig
): boolean {
  const baseTransaction = view.state.tr.deleteSelection();
  if (!baseTransaction.docChanged) {
    return false;
  }

  const { tr, hasChanges } = applyCanonicalTopLevelFieldAttrs(
    schemaConfig,
    baseTransaction.doc,
    baseTransaction
  );
  view.dispatch(hasChanges ? tr : baseTransaction);
  return true;
}

export const SchemaAwareDelete = Extension.create<SchemaAwareDeleteOptions>({
  name: "schemaAwareDelete",

  addOptions() {
    return {
      schemaConfig: null,
    };
  },

  addProseMirrorPlugins() {
    const schemaConfig = this.options.schemaConfig;
    if (!schemaConfig) return [];

    return [
      new Plugin({
        key: schemaAwareDeletePluginKey,
        props: {
          handleKeyDown(view, event) {
            if (
              (event.key !== "Backspace" && event.key !== "Delete") ||
              !shouldHandleSchemaAwareDelete(
                view,
                event.key === "Backspace" ? "backward" : "forward"
              )
            ) {
              return false;
            }

            event.preventDefault();
            return deleteSelectionWithCanonicalFieldAttrs(view, schemaConfig);
          },
          handleDOMEvents: {
            beforeinput(view, event) {
              const inputEvent = event as InputEvent;
              const inputType = inputEvent.inputType;
              const deleteType =
                inputType === "deleteByCut"
                  ? "cut"
                  : inputType === "deleteContentBackward"
                    ? "backward"
                    : inputType === "deleteContentForward" || inputType === "deleteByDrag"
                      ? "forward"
                      : null;
              if (!deleteType || !shouldHandleSchemaAwareDelete(view, deleteType)) {
                return false;
              }

              inputEvent.preventDefault();
              return deleteSelectionWithCanonicalFieldAttrs(view, schemaConfig);
            },
          },
        },
      }),
    ];
  },
});
